import angular from 'angular';
import moment from 'moment';
import { ModalConfirmService } from '../../../../shared/components/modal-confirm/modal.confirm.service';
import {
    isItemElegivelParaReversao,
    IUploadDocumentoGet,
    IUploadDocumentoHistorico,
    IUploadDocumentoItem,
    UploadDocumentoHistoricoTipo,
    UploadDocumentoItemStatus,
    UploadDocumentoStatus,
    UploadDocumentoStatusReversao,
} from '../models/upload-documentos-em-lote.service.model';
import { UploadDocumentosEmLoteService } from '../upload-documentos-em-lote.service';
import { IHistoricoUploadItemView } from './components/historico-upload/models/historico-upload.model';

/**
 * Detalhe do lote: resumo, colaboradores por situação, histórico, exportação e
 * reversão.
 *
 * Os colaboradores são particionados no cliente por `item.status`: cada um é um
 * upload independente, que pode falhar por motivo próprio.
 */
export class UploadDocumentosEmLoteShowController {

    static $inject = [
        '$rootScope',
        '$scope',
        '$state',
        'entity',
        'UploadDocumentosEmLoteService',
        'ModalConfirmService',
        'NewToaster',
    ];

    public busy = false;
    public itens: IUploadDocumentoItem[] = [];
    public carregandoItens = false;
    public itensLoadError = false;

    /**
     * Particoes materializadas, e nao funcoes chamadas do template: um metodo que
     * devolve `this.itens.filter(...)` cria um array novo a cada digest e o
     * binding nunca estabiliza. Recalculadas quando os itens mudam.
     */
    public gerados: IUploadDocumentoItem[] = [];
    public comErro: IUploadDocumentoItem[] = [];
    public revertidos: IUploadDocumentoItem[] = [];
    public pendentes: IUploadDocumentoItem[] = [];
    public elegiveisParaReversao: IUploadDocumentoItem[] = [];

    public historicoAberto = false;
    public historicoItens: IHistoricoUploadItemView[] = [];
    public carregandoHistorico = false;
    public erroHistorico = false;

    public exportando = false;

    public isModoReversaoParcial = false;
    public itensSelecionados: IUploadDocumentoItem[] = [];
    private isRefreshingAfterReversao = false;
    private reversaoPendenteTipo: 'total' | 'parcial' | null = null;
    private readonly resumoVazio = {};

    constructor(
        public $rootScope: angular.IRootScopeService,
        public $scope: angular.IScope,
        public $state: angular.ui.IStateService,
        public entity: IUploadDocumentoGet,
        public UploadDocumentosEmLoteService: UploadDocumentosEmLoteService,
        public ModalConfirmService: ModalConfirmService,
        public NewToaster: { pop: (opts: { type: string; title: string; body?: string }) => void },
    ) {
        this.carregarItens();

        $scope.$on('meurh_uploadsdocumentos_get_loaded', (_event, data: IUploadDocumentoGet) => {
            this.entity = data;

            if (!this.isRefreshingAfterReversao) {
                // releitura depois de um erro: sem reversao possivel, a selecao
                // parcial nao tem mais o que confirmar.
                if (this.isModoReversaoParcial && !this.entity?.pode_reverter) {
                    this.cancelarReversaoParcial();
                }

                return;
            }

            const tipo = this.reversaoPendenteTipo;

            this.cancelarReversaoParcial();
            this.carregarItens();
            this.busy = false;
            this.reversaoPendenteTipo = null;
            this.isRefreshingAfterReversao = false;

            this.NewToaster.pop({
                type: 'success',
                title: `Reversão ${tipo} iniciada com sucesso.`,
            });
        });

        $scope.$on('meurh_uploadsdocumentos_get_error', () => {
            if (!this.isRefreshingAfterReversao) {
                return;
            }

            this.busy = false;
            this.reversaoPendenteTipo = null;
            this.isRefreshingAfterReversao = false;

            this.NewToaster.pop({
                type: 'error',
                title: 'A reversão foi iniciada, mas ocorreu um erro ao atualizar os dados da tela.',
            });
        });

        // busy segue ligado: a tela so libera quando o get() de refresh volta.
        $scope.$on('meurh_uploadsdocumentos_reversao_loaded', () => {
            this.isRefreshingAfterReversao = true;
            this.UploadDocumentosEmLoteService.get(this.entity.uploaddocumento);
        });

        $scope.$on('meurh_uploadsdocumentos_reversao_error', (_event, error) => {
            const tipo = this.reversaoPendenteTipo;

            this.busy = false;
            this.reversaoPendenteTipo = null;
            this.isRefreshingAfterReversao = false;

            this.NewToaster.pop({
                type: 'error',
                title: `Ocorreu um erro ao confirmar a reversão ${tipo || ''}.`.trim(),
                body: this.getErrorMessage(error),
            });

            // o erro pode vir de um estado que a tela nao conhece (outra aba ja
            // reverteu, documento assinado depois de abrir): rele lote e itens.
            this.UploadDocumentosEmLoteService.get(this.entity.uploaddocumento)
                .catch(() => { /* releitura silenciosa: o toast acima ja avisou */ });
            this.carregarItens();
        });

        $scope.$on('meurh_uploadsdocumentos_exportacao_loaded', (_event, blob: Blob) => {
            this.exportando = false;
            this.baixarArquivo(blob);
        });

        $scope.$on('meurh_uploadsdocumentos_exportacao_error', (_event, error) => {
            this.exportando = false;
            this.NewToaster.pop({
                type: 'error',
                title: 'Ocorreu um erro ao exportar os detalhes do lote.',
                body: this.getErrorMessage(error),
            });
        });
    }

    // ---------- itens ----------

    carregarItens() {
        this.carregandoItens = true;
        this.itensLoadError = false;

        this.UploadDocumentosEmLoteService.getItens(this.entity.uploaddocumento)
            .then((itens) => {
                this.itens = itens || [];
                this.particionarItens();
            })
            .catch(() => {
                this.itens = [];
                this.itensLoadError = true;
                this.particionarItens();
            })
            .finally(() => this.carregandoItens = false);
    }

    particionarItens() {
        this.gerados = this.itens.filter((item) => item.status === UploadDocumentoItemStatus.Sucesso);
        this.comErro = this.itens.filter((item) => item.status === UploadDocumentoItemStatus.Erro);
        this.revertidos = this.itens.filter((item) => item.status === UploadDocumentoItemStatus.Revertido);
        this.pendentes = this.itens.filter((item) => item.status === UploadDocumentoItemStatus.Pendente);
        this.elegiveisParaReversao = this.itens.filter(isItemElegivelParaReversao);

        // itens relidos no modo parcial: a selecao so guarda quem ainda pode ser
        // revertido, para o confirmar nao reenviar documento que ja saiu.
        const elegiveis = new Set(this.elegiveisParaReversao.map((item) => item.trabalhador));
        this.itensSelecionados = this.itensSelecionados.filter((item) => elegiveis.has(item.trabalhador));

        // sem o que selecionar (itens nao carregaram, ou sobrou menos de dois
        // elegiveis), o modo parcial so deixaria um confirmar desabilitado.
        if (this.isModoReversaoParcial && (this.itensLoadError || !this.hasItensElegiveisParaReversaoParcial())) {
            this.cancelarReversaoParcial();
        }
    }

    // ---------- resumo ----------

    getResumo() {
        // o fallback e uma instancia fixa: `{}` novo a cada chamada nao deixa o
        // binding do template estabilizar.
        return this.entity?.log?.resumo || this.resumoVazio;
    }

    temErroNoLote() {
        return this.entity?.status === UploadDocumentoStatus.Erro;
    }

    estaProcessando() {
        return this.entity?.status === UploadDocumentoStatus.Criado
            || this.entity?.status === UploadDocumentoStatus.EmProcessamento;
    }

    /** Reversão na fila ou rodando no worker. */
    estaRevertendo() {
        return this.entity?.statusreversao === UploadDocumentoStatusReversao.EmAndamento;
    }

    getFiltroLabel(campo: 'estabelecimento' | 'departamento' | 'lotacao') {
        const valor: any = this.entity ? (this.entity as any)[campo] : null;

        if (!valor) {
            return 'Todos';
        }

        return valor.nomefantasia || valor.nome || 'Todos';
    }

    // ---------- histórico ----------

    abrirHistorico() {
        this.historicoAberto = true;
        this.carregandoHistorico = true;
        this.erroHistorico = false;

        this.UploadDocumentosEmLoteService.getHistorico(this.entity.uploaddocumento)
            .then((historicos) => {
                this.historicoItens = this.transformarHistorico(historicos || []);
            })
            .catch(() => {
                this.erroHistorico = true;
            })
            .finally(() => this.carregandoHistorico = false);
    }

    fecharHistorico() {
        this.historicoAberto = false;
    }

    // ---------- exportação ----------

    exportarDetalhes() {
        if (this.exportando) {
            return;
        }

        this.exportando = true;
        this.UploadDocumentosEmLoteService.exportarXlsx(this.entity.uploaddocumento);
    }

    // ---------- reversão ----------

    /**
     * O `pode_reverter` do backend olha só a situação do lote; se ainda existe
     * documento para excluir é o que os itens já carregados dizem.
     */
    canReverter() {
        return !!this.entity?.pode_reverter
            && !this.busy
            && !this.carregandoItens
            && this.elegiveisParaReversao.length > 0;
    }

    hasItensElegiveisParaReversaoParcial() {
        return this.elegiveisParaReversao.length > 1;
    }

    iniciarReversaoParcial() {
        if (!this.canReverter() || !this.hasItensElegiveisParaReversaoParcial()) {
            return;
        }

        this.isModoReversaoParcial = true;
        this.itensSelecionados = [];
    }

    cancelarReversaoParcial() {
        this.isModoReversaoParcial = false;
        this.itensSelecionados = [];
    }

    atualizarSelecao(selectedItems: IUploadDocumentoItem[]) {
        this.itensSelecionados = selectedItems || [];
    }

    hasItensSelecionados() {
        return this.itensSelecionados.length > 0;
    }

    confirmarReversaoTotal() {
        if (!this.canReverter()) {
            return;
        }

        const confirm = this.ModalConfirmService.open({}, 'reversão do upload em lote', false, {
            title: 'Reverter totalmente',
            confirmText: 'Tem certeza que deseja reverter este upload? Os documentos gerados serão excluídos.',
            alertInfo: {
                header: 'Atenção',
                text: 'Documentos já assinados não são excluídos e permanecerão com o colaborador. '
                    + 'A reversão é processada em segundo plano e você receberá um e-mail quando terminar.',
            },
            closeButton: 'Cancelar',
            confirmButton: 'Sim, reverter totalmente',
        });

        confirm.result
            .then(() => this.submitReversao({}, 'total'))
            .catch(() => { /* modal cancelada */ });
    }

    confirmarReversaoParcial() {
        if (!this.canReverter() || !this.hasItensSelecionados()) {
            return;
        }

        const confirm = this.ModalConfirmService.open({}, 'reversão parcial do upload em lote', false, {
            title: 'Reverter parcialmente',
            confirmText: `Tem certeza que deseja reverter o upload de ${this.itensSelecionados.length} colaborador(es)?`
                + ' Os documentos gerados serão excluídos.',
            alertInfo: {
                header: 'Atenção',
                text: 'Documentos já assinados não são excluídos e permanecerão com o colaborador. '
                    + 'A reversão é processada em segundo plano e você receberá um e-mail quando terminar.',
            },
            closeButton: 'Cancelar',
            confirmButton: 'Sim, reverter selecionados',
        });

        confirm.result
            .then(() => this.submitReversao({
                trabalhadores: this.itensSelecionados.map((item) => ({ trabalhador: item.trabalhador })),
            }, 'parcial'))
            .catch(() => { /* modal cancelada */ });
    }

    // ---------- privados ----------

    private transformarHistorico(historicos: IUploadDocumentoHistorico[]): IHistoricoUploadItemView[] {
        return historicos.map((historico) => {
            const resumo = historico.log?.resumo || {};
            const temErro = !!historico.erro;

            let titulo = 'Upload em lote';
            let indicatorClass = 'indicator--success';
            let descricao = `${resumo.qtd_documentos_gerados || 0} documento(s) gerado(s)`
                + `, ${resumo.qtd_erros || 0} com erro.`;

            if (historico.tipo === UploadDocumentoHistoricoTipo.ReversaoParcial) {
                titulo = 'Reversão parcial';
                indicatorClass = 'indicator--info';
                descricao = `${resumo.qtd_documentos_revertidos || 0} documento(s) revertido(s).`;
            }

            if (historico.tipo === UploadDocumentoHistoricoTipo.ReversaoTotal) {
                titulo = 'Reversão total';
                indicatorClass = 'indicator--info';
                descricao = `${resumo.qtd_documentos_revertidos || 0} documento(s) revertido(s).`;
            }

            if (temErro) {
                indicatorClass = 'indicator--warning';
                descricao = historico.erro?.mensagem || descricao;
            }

            return {
                id: historico.uploaddocumentohistorico,
                titulo,
                dataHora: historico.created_at
                    ? moment(historico.created_at).format('DD/MM/YYYY [às] HH:mm')
                    : '',
                responsavel: historico.created_by?.nome || '',
                descricao,
                indicatorClass,
            };
        });
    }

    private baixarArquivo(blob: Blob) {
        const nome = `upload_documentos_lote_${moment().format('DD-MM-YYYY')}.xlsx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');

        document.body.appendChild(a);
        a.style.display = 'none';
        a.href = url;
        a.download = nome;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
    }

    private submitReversao(
        payload: { trabalhadores?: { trabalhador: string }[] },
        tipo: 'total' | 'parcial',
    ) {
        this.busy = true;
        this.reversaoPendenteTipo = tipo;
        this.isRefreshingAfterReversao = false;

        this.UploadDocumentosEmLoteService.reverter(this.entity.uploaddocumento, payload);
    }

    private getErrorMessage(error: any) {
        return error?.data?.message || error?.data?.mensagem || error?.data?.erro || undefined;
    }
}
